// Configuration service
// Loads configuration from deployed config.json

// Free trial configuration (matches backend pricing.py)
export const FREE_TRIAL_ENABLED = true;  // Master switch to enable/disable all free trial features
export const DEFAULT_SIGNUP_CREDITS = 500;  // Free credits granted to new users on signup

interface StripeProduct {
  productId: string;
  priceId: string;
}

interface StripeConfig {
  publishableKey: string;
  products: {
    credits: StripeProduct;
    creditsRefill: StripeProduct;
  };
}

interface Config {
  customerTag: string;
  environment: string;
  apiBaseUrl: string;
  gcpProject: string;
  gcpRegion: string;
  frontendUrl: string;
  stripe?: StripeConfig;
  firebaseConfig?: any; // For local development
}

interface DeployedConfig {
  customerTag: string;
  environment: string;
  gcpProject: string;
  apiUrl: string;
  frontendUrl?: string;
  gcpRegion?: string;
  stripe?: StripeConfig;
  firebaseConfig?: any; // For local development
}

// Cache the loaded configuration
let loadedConfig: Config | null = null;
let configPromise: Promise<Config> | null = null;

// Load configuration from config.json
async function loadConfigFromFile(): Promise<Config> {
  try {
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error('Failed to load config.json');
    }
    
    const deployedConfig: DeployedConfig = await response.json();
    
    return {
      customerTag: deployedConfig.customerTag || "DEMO001",
      environment: deployedConfig.environment,
      apiBaseUrl: deployedConfig.apiUrl,
      gcpProject: deployedConfig.gcpProject || "passio-duplo-dev",
      gcpRegion: deployedConfig.gcpRegion || process.env.REACT_APP_GCP_REGION || "us-central1",
      frontendUrl: deployedConfig.frontendUrl || window.location.origin,
      stripe: deployedConfig.stripe,
      firebaseConfig: deployedConfig.firebaseConfig, // Pass through Firebase config if present
    };
  } catch (error) {

    // Fallback to deriving from hostname
    return getFallbackConfig();
  }
}

// Fallback configuration when config.json is not available
function getFallbackConfig(): Config {
  const hostname = window.location.hostname;
  
  // Handle localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      customerTag: "DEMO001",
      environment: "dev",
      apiBaseUrl: 'http://localhost:8001',
      gcpProject: "passio-duplo-dev",
      gcpRegion: "us-central1",
      frontendUrl: 'http://localhost:3000',
    };
  }
  
  // For production, derive from hostname
  const customerTag = hostname.split("-")[0].toUpperCase() || "DEMO001";
  // In production, the API is served from the same domain as the frontend
  // So we just use the current origin
  const apiUrl = window.location.origin;
    
  return {
    customerTag,
    environment: "prod",
    apiBaseUrl: apiUrl,
    gcpProject: "passio-duplo-dev",
    gcpRegion: "us-central1",
    frontendUrl: window.location.origin,
  };
}

// Get config synchronously if already loaded, otherwise returns fallback
export function getConfig(): Config {
  if (loadedConfig) {
    return loadedConfig;
  }
  
  // If we're still loading, return fallback for now
  return getFallbackConfig();
}

// Load config asynchronously
export async function loadConfig(): Promise<Config> {
  if (loadedConfig) {
    return loadedConfig;
  }
  
  if (!configPromise) {
    configPromise = loadConfigFromFile().then(config => {
      loadedConfig = config;
      return config;
    });
  }
  
  return configPromise;
}

// Get Stripe configuration
export function getStripeConfig(): StripeConfig | undefined {
  const config = getConfig();
  return config.stripe;
}

// Get Stripe publishable key
export function getStripePublishableKey(): string | undefined {
  return getStripeConfig()?.publishableKey;
}

// Get Stripe product/price IDs
export function getStripeProductIds() {
  const stripe = getStripeConfig();
  if (!stripe) return null;
  
  return {
    credits: stripe.products.credits,
    creditsRefill: stripe.products.creditsRefill,
  };
}

// Get Stripe price ID by product type
export function getStripePriceId(productType: 'credits' | 'creditsRefill'): string | undefined {
  return getStripeConfig()?.products[productType].priceId;
}

// Get Stripe product ID by product type
export function getStripeProductId(productType: 'credits' | 'creditsRefill'): string | undefined {
  return getStripeConfig()?.products[productType].productId;
}

export function getResourceUrl(resource: string): string {
  const config = getConfig();
  switch (resource) {
    case "gcp-console":
      return `https://console.cloud.google.com/home/dashboard?project=${config.gcpProject}`;
    case "cloud-sql":
      return `https://console.cloud.google.com/sql/instances?project=${config.gcpProject}`;
    case "gke":
      return `https://console.cloud.google.com/kubernetes/list/overview?project=${config.gcpProject}`;
    case "logs":
      return `https://console.cloud.google.com/logs/query?project=${config.gcpProject}`;
    default:
      return "#";
  }
}